<template>
  <sign-in class="saml-choose-account">
    <template v-if="accounts.length > 0">
      <h1 class="_title">
        {{ $t('samlChooseAccount.title') }}
      </h1>
      <p class="_body">
        {{ $t('samlChooseAccount.body', { email }) }}
      </p>
      <div class="_accounts">
        <m-card
          v-for="account in accounts"
          :key="account.uid"
          padding-xs
          :level="1"
          clickable
          class="_account-row"
          @click="select(account.uid)"
        >
          <div class="_account-row-inner">
            <m-avatar
              :text="account.name"
              :amount-chars="1"
              :size="22"
            />
            <div class="_name">
              {{ account.name }}
            </div>
            <m-btn
              hide-border
              small
              light
              class="_select"
              @click.stop="select(account.uid)"
            >
              {{ $t('samlChooseAccount.select') }}
            </m-btn>
          </div>
        </m-card>
      </div>
    </template>
    <template v-else>
      <m-alert type="error">
        {{ $t('samlChooseAccount.invalid') }}
      </m-alert>
      <m-btn
        block
        class="_home-btn"
        @click="goHome"
      >
        {{ $t('samlChooseAccount.backToLogin') }}
      </m-btn>
    </template>
  </sign-in>
</template>

<script setup>
import SignIn from '@/components/SignIn.vue';
import { computed } from 'vue';
import { encodeURLValues } from '@/lib/url';
import { getSentinelUrl } from '@/api/sentinel';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

const email = computed(() => route.query.email ?? route.query.samlEmail ?? '');

const candidates = computed(() => {
  if (typeof route.query.candidates !== 'string' || route.query.candidates === '') {
    return [];
  }
  try {
    const base64 = route.query.candidates.replace(/-/g, '+').replace(/_/g, '/');
    const parsed = JSON.parse(atob(base64));
    return Array.isArray(parsed) ? parsed.filter((candidate) => candidate.slug) : [];
  } catch {
    return [];
  }
});

const accounts = computed(() => candidates.value.map((candidate) => ({
  uid: candidate.slug,
  name: candidate.companyName || candidate.slug,
})));

const select = (slug) => {
  if (route.query.flow === 'msteams') {
    const params = {
      samlEmail: email.value,
      slug,
      provider: route.query.provider ?? '',
      loginHint: route.query.loginHint ?? '',
      tenant: route.query.tenant ?? '',
    };
    window.location.href = getSentinelUrl(`/msteams/auth?${encodeURLValues(params)}`);
    return;
  }

  window.location.href = getSentinelUrl(`/saml/login?${encodeURLValues({ email: email.value, slug })}`);
};

const goHome = () => {
  router.push('/');
};
</script>

<style scoped lang="scss" type="text/scss">
  .saml-choose-account {
    ._title {
      margin: 0 0 1.2rem;
      font-size: $font-size-8;
      font-weight: $font-weight-bold;
      color: $font-color-primary;
    }

    ._body {
      margin: 0 0 2rem;
      color: $font-color-secondary;
      font-size: $font-size-4;
      line-height: 1.55;
    }

    ._accounts {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
    }

    ._account-row {
      cursor: pointer;

      &:hover {
        background-color: $hover-color;
      }

      ._account-row-inner {
        display: flex;
        align-items: center;
        gap: 1rem;

        ._name {
          flex: 1;
          overflow: hidden;
          font-size: $font-size-5;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ._select {
          flex-shrink: 0;
          color: $font-color-secondary;
        }
      }
    }

    ._home-btn {
      margin-top: 1.6rem;
    }
  }
</style>
